<script setup>
import moment from "moment/moment";
import {computed, onMounted, ref, watch} from "vue";
import Avatar from "vue3-avatar";
import axios from 'axios'

import FlaggedItem from "./FlaggedItem.vue";
import BulkAssignInspections from "./BulkAssignInspections.vue";
import CompletedInspections from "./CompletedInspections.vue";

import useDashboardMapFilter from "@/components/home/dashboard/composables/useDashboardMapFilter";
import useMapImagery from "@/components/maps/shared/composables/useMapImagery.js";
import forestreeHelpers from "@/helpers/forestreeHelpers.js";
import AddressSearch from "@/components/maps/shared/AddressSearch.vue";


const props = defineProps({
    assignedInspections: {},
    access: {},
    permissions: {},
    tasks: {},
    inspCount: {},
    revCount: {},
    showReview: {},
    showPlanting: {},
});
const inspections = ref([])
const selectedUser = ref([])
const defaults = ref(null)
const filteredLength = ref(0)
const users = ref(null)
const teams = ref([])
const hideThings = ref(false)
const editFlag = ref(false)
const today = ref(moment().format("dddd Do MMM YYYY"))
const sortBy = ref("created_at")
const sortAsc = ref(true)
const showMap = ref(false)
const forMap = ref(null)
const openActions = ref(false)
const showBulkAssign = ref(false)
const noMap = ref(false)
const page = ref(1)
const flagType = ref('inspection')
const inspectionCount = ref(0)
const reviewCount = ref(0)
const plantingCount = ref(0)
const unassignedCount = ref(null)

const {
    map,
    osm,
    imageService,
    aerialGroup,
    labels,
    satellite,
    aerial,
    selectedFeatures,
    createMap,
    addTreesGL,
    enablePolygonDraw,
    restart,
    polygonSource
} = useDashboardMapFilter()
const {baseMapSwitch, addAdditionalLayers} = useMapImagery({
    map,
    osmLayer: osm,
    imageService: imageService,
    aerialGroup,
    labelLayer: labels,
    satelliteLayer: satellite,
    aerialLayer: aerial,
})

watch(() => props.assignedInspections, () => {
    hideThings.value = true;
    inspections.value = props.assignedInspections ?? [];
});

const emits = defineEmits(["refresh"]);

const handleRemoveFlag = (val) => {
    inspections.value = [];
    axios
        .post("/api/flag/remove?flagType=" + flagType.value, {
            flagId: val,
            restrict: hideThings.value,
        })
        .then((response) => {
            if (props.assignedInspections) {
                emits("refresh");
            } else {
                inspections.value = response.data?.flaggedTrees ?? [];
                users.value = response.data.users;
                teams.value = response.data.teams;
            }
        })
        .catch((error) => {
            console.log(error);
        });
};

const filterInspections = computed(() => {
    let filteredList = inspections.value?.filter((f) => {
        return f.type === flagType.value;
    });

    if (flagType.value === 'inspection') {
        if (selectedUser.value.length) {
            filteredList = filteredList.filter((work) => {
                if (selectedUser.value.includes(-99)) {
                    return (
                        (!work.assignments || work.assignments.length === 0) ||
                        (work.assignments && work.assignments.filter((user) =>
                            selectedUser.value.includes(user.user_id)
                        ).length)
                    );
                } else {
                    return work.assignments && work.assignments.filter((user) =>
                        selectedUser.value.includes(user.user_id)
                    ).length;
                }
            });
        }
    }

    if (flagType.value === 'review') {
        if (selectedUser.value.length) {
            filteredList = filteredList.filter((work) => {
                if (selectedUser.value.includes(-99)) {
                    return (
                        (!work.review_assignments || work.review_assignments.length === 0) ||
                        (work.review_assignments && work.review_assignments.filter((user) =>
                            selectedUser.value.includes(user.user_id)
                        ).length)
                    );
                } else {
                    return work.review_assignments && work.review_assignments.filter((user) =>
                        selectedUser.value.includes(user.user_id)
                    ).length;
                }
            });
        }
    }

    if (flagType.value === 'planting') {
        if (selectedUser.value.length) {
            filteredList = filteredList.filter((work) => {
                if (selectedUser.value.includes(-99)) {
                    return (
                        (!work.assignments || work.assignments.length === 0) ||
                        (work.assignments && work.assignments.filter((user) =>
                            selectedUser.value.includes(user.user_id)
                        ).length)
                    );
                } else {
                    return work.assignments && work.assignments.filter((user) =>
                        selectedUser.value.includes(user.user_id)
                    ).length;
                }
            });
        }
    }

    filteredLength.value = filteredList.length;
    forMap.value = filteredList;

    return sortArray(filteredList);
});

watch(filterInspections, () => {
    addTreesGL(forMap.value);
})

const userTotals = computed(() => {
    if (users.value) {
        for (var i = 0; i < users.value.active_users.length; i++) {
            users.value.active_users[i].jobs = 0;
            let all = [];

            if (flagType.value === 'inspection') {
                all = inspections.value.filter((work) => {
                    return work.type === flagType.value && work.assignments && work.assignments.filter(
                        (user) => user.user_id === users.value.active_users[i].id
                    ).length;
                });
            }
            if (flagType.value === 'review') {
                all = inspections.value.filter((work) => {
                    return work.type === flagType.value && work.review_assignments && work.review_assignments.filter(
                        (user) => user.user_id === users.value.active_users[i].id
                    ).length;
                });
            }
            if (flagType.value === 'planting') {
                all = inspections.value.filter((work) => {
                    return work.type === flagType.value && work.assignments && work.assignments.filter(
                        (user) => user.user_id === users.value.active_users[i].id
                    ).length;
                });
            }
            if (all.length) {
                users.value.active_users[i].jobs = all.length;
            }

            let unassigned = [];
            if (flagType.value === 'inspection') {
                unassigned = inspections.value.filter((work) => {
                    return work.type === flagType.value && (!work.assignments || work.assignments.length === 0);
                });
            }
            if (flagType.value === 'review') {
                unassigned = inspections.value.filter((work) => {
                    return work.type === flagType.value && (!work.review_assignments || work.review_assignments.length === 0);
                });
            }
            if (flagType.value === 'planting') {
                unassigned = inspections.value.filter((work) => {
                    return work.type === flagType.value && (!work.assignments || work.assignments.length === 0);
                });
            }

            if (unassigned.length) {
                unassignedCount.value = unassigned.length;
            } else {
                unassignedCount.value = null;
            }
        }
        return users.value.active_users.filter((user) => user.jobs > 0);
    }
});

const initMap = () => {
    let homeCentre = {
        lat: defaults.value.lat,
        lng: defaults.value.lng,
        zoom: defaults.value.zoom,
    };
    createMap(homeCentre, true);
    addAdditionalLayers();
    document.addEventListener("click", close);
    addTreesGL(forMap.value);
};

const bulkUpdated = () => {
    flaggedInspections();
    showBulkAssign.value = false;
};

const updateMap = () => {
    addTreesGL(forMap.value);
};

const clearArea = () => {
    if (polygonSource.value) {
        restart();
    }
};

const close = (e) => {
    // if (!this.$refs.actions.contains(e.target)) {
    //   this.openActions = false;
    // }
};
const openActionsMenu = () => {
    openActions.value = !openActions.value;
};
const toggleToArray = (userId) => {
    if (selectedUser.value.includes(userId)) {
        selectedUser.value.splice(selectedUser.value.indexOf(userId), 1);
    } else {
        selectedUser.value.push(userId);
    }
};

const flaggedInspections = (loadmap = false) => {
    axios
        .get("/api/flag/show")
        .then((response) => {
            inspections.value = response.data?.flaggedTrees ?? [];
            users.value = response.data.users;
            defaults.value = response.data.defaults;
            inspectionCount.value = response.data.inspectionCount;
            reviewCount.value = response.data.reviewCount;
            plantingCount.value = response.data.plantingCount;
            if (loadmap) {
                initMap();
            }
        })
        .catch((error) => {
            console.log(error);
        });
};
const downloadData = () => {
    window.open("/api/flag/export?flagType=" + flagType.value, "_blank");
};

function  sortArray(array) {
    if (sortBy.value === "created_at") {
        if (sortAsc.value) {
            return array.sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
        }
        if (!sortAsc.value) {
            return array.sort(
                (b, a) => new Date(b.created_at) - new Date(a.created_at)
            );
        }
    }

    if (sortBy.value === "due_date") {
        if (sortAsc.value) {
            return array.sort(
                (a, b) => new Date(b.next_inspection) - new Date(a.next_inspection)
            );
        }
        if (!sortAsc.value) {
            return array.sort(
                (b, a) => new Date(b.next_inspection) - new Date(a.next_inspection)
            );
        }
    }

    return array;
}

onMounted(async () => {
    if (props.assignedInspections) {
        hideThings.value = true;
        console.log(props.assignedInspections);
        console.log(hideThings.value);
        inspections.value = props.assignedInspections ?? [];
        noMap.value = false;  // Set to false to allow map updates
    } else {
        await flaggedInspections(true);
    }
})
</script>
<template>
    <div>
        <div
            class="flex justify-between flex-wrap lg:flex-no-wrap pt-2 pb-5 w-full border-b border-slate-200"
        >
            <div class="">
                <span
                    v-if="hideThings"
                    @click.prevent="page = 1; flagType = 'inspection'"
                    :class="
                        page === 1 && flagType === 'inspection'
                            ? 'text-blue-700 rounded-lg bg-slate-100'
                            : 'text-slate-500'
                    "
                    class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                >
                    Flagged Inspections
                    <span
                        :class="
                            inspCount > 0
                                ? 'bg-red-600 text-red-100'
                                : 'bg-slate-100 text-slate-500'
                        "
                        class="hidden px-2 rounded-full font-semibold md:inline-block"
                    >{{ inspCount }}</span
                    >
                </span>
                <span
                    v-if="hideThings && (permissions && permissions.flag_review) && showReview"
                    @click.prevent="page = 1; flagType = 'review'"
                    :class="
                        page === 1 && flagType === 'review'
                            ? 'text-blue-700 rounded-lg bg-slate-100'
                            : 'text-slate-500'
                    "
                    class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                >
                    Flagged Reviews
                    <span
                        :class="
                            revCount > 0
                                ? 'bg-red-600 text-red-100'
                                : 'bg-slate-100 text-slate-500'
                        "
                        class="hidden px-2 rounded-full font-semibold md:inline-block"
                    >{{ revCount }}</span
                    >
                </span>
                <span
                    v-if="hideThings && showPlanting"
                    @click.prevent="page = 1; flagType = 'planting'"
                    :class="
                        page === 1 && flagType === 'planting'
                            ? 'text-blue-700 rounded-lg bg-slate-100'
                            : 'text-slate-500'
                    "
                    class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                >
                    Flagged Plantings
                    <span
                        :class="
                            plantingCount > 0
                                ? 'bg-red-600 text-red-100'
                                : 'bg-slate-100 text-slate-500'
                        "
                        class="hidden px-2 rounded-full font-semibold md:inline-block"
                    >{{ plantingCount }}</span
                    >
                </span>
                <span
                    v-if="hideThings"
                    @click.prevent="page = 2"
                    :class="
                        page === 2
                            ? 'text-blue-700 rounded-lg bg-slate-100'
                            : 'text-slate-500'
                    "
                    class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                >
                    Completed Inspections
                </span>
                <span
                    v-if="!hideThings"
                    @click.prevent="flagType = 'inspection'"
                    :class="
                        flagType === 'inspection'
                            ? 'text-blue-700 rounded-lg bg-slate-100'
                            : 'text-slate-500'
                    "
                    class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                >
                    Flagged Inspections
                </span>
                <span
                    v-if="!hideThings && (defaults && defaults.flag_review)"
                    @click.prevent="flagType = 'review'"
                    :class="
                        flagType === 'review'
                            ? 'text-blue-700 rounded-lg bg-slate-100'
                            : 'text-slate-500'
                    "
                    class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                >
                    Flagged Reviews
                </span>
                <span
                    v-if="!hideThings && showPlanting"
                    @click.prevent="flagType = 'planting'"
                    :class="
                        flagType === 'planting'
                            ? 'text-blue-700 rounded-lg bg-slate-100'
                            : 'text-slate-500'
                    "
                    class="w-full text-center md:w-auto mr-0 md:mr-8 p-3 font-semibold cursor-pointer hover:text-blue-600"
                >
                    Flagged Plantings
                </span>
            </div>
            <div class="">
                <a
                    v-if="inspections.length > 0 && page === 1"
                    :href="flagType === 'planting' ? '/vacant-map?showFlagged=true' : '/map?showFlagged=true'"
                    class="text-white rounded-lg bg-blue-600 w-full text-center md:w-auto mr-0 md:mr-3 p-3 font-semibold cursor-pointer hover:text-blue-200"
                >
                    Show Map
                </a>
            </div>
        </div>
        <div class="openInspectionPage flex flex-col" v-if="page === 1">
            <div class="flex">
                <div v-if="flagType === 'inspection'" class="w-full">
                    <p class="my-3 text-xl text-slate-600">
                        Flagged for Inspection ({{ inspectionCount }})
                    </p>
                    <p class="my-3 text-md text-slate-500">
                        Trees that have been flagged by users for Inspection.
                        Flags will be removed after the tree is inspected.
                    </p>
                </div>
                <div v-if="flagType === 'review'" class="w-full">
                    <p class="my-3 text-xl text-slate-600">
                        Flagged for Review ({{ reviewCount }})
                    </p>
                    <p class="my-3 text-md text-slate-500">
                        Trees that have been flagged by users for Review.
                    </p>
                </div>
                <div v-if="flagType === 'planting'" class="w-full">
                    <p class="my-3 text-xl text-slate-600">
                        Flagged for Planting ({{ plantingCount }})
                    </p>
                    <p class="my-3 text-md text-slate-500">
                        Planting Locations that have been flagged by users for Review.
                    </p>
                </div>
            </div>
            <div
                v-if="users && users.active_users.length > 0"
                class="py-4 flex flex-no-wrap lg:flex-wrap overflow-x-auto items-center"
            >
                <p
                    class="w-full whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-2"
                >
                    Jobs by User
                </p>
                <template
                    v-for="user in userTotals"
                    :key="user.id"
                >
                    <span
                        @click.prevent="toggleToArray(user.id)"
                        :class="
                            selectedUser.includes(user.id)
                                ? 'bg-green-200 hover:bg-green-300'
                                : 'bg-slate-100 hover:bg-slate-200'
                        "
                        class="flex rounded-lg text-slate-500 text-center w-auto mr-3 mt-1 p-3 cursor-pointer hover:text-blue-600"
                    >
                        <avatar
                            :title="user.name"
                            class="cursor-pointer"
                            :name="user.name.replace(/([0-9])/g, ' $1').trim()"
                            :background="forestreeHelpers.getAvatarColor(user.name.replace(/([0-9])/g, ' $1').trim())"
                            color="#fff"
                            :size="32"
                            :customAvatarStyle="{
                                fontFamily: 'Arial',
                                fontSize: '12px',
                            }"
                        >
                        </avatar>
                        <p class="font-semibold px-2 items-center text-lg">
                            {{ user.jobs }}
                        </p>
                    </span>

                </template>
                <span
                    v-if="unassignedCount > 0"
                    @click.prevent="toggleToArray(-99)"
                    :class="
                        selectedUser.includes(-99)
                            ? 'bg-green-200 hover:bg-green-300'
                            : 'border border-slate-300 hover:bg-slate-100'
                    "
                    class="flex rounded-lg text-slate-500 text-center w-auto mr-3 mt-1 p-3 cursor-pointer hover:text-blue-600"
                >
                    <avatar
                        title="Unassigned"
                        class="cursor-pointer"
                        name="Unassigned"
                        background="#718096"
                        color="#FFFFFF"
                        :size="32"
                        :customAvatarStyle="{
                            fontFamily: 'Arial',
                            fontSize: '12px',
                        }"
                    >
                    </avatar>
                    <p class="font-semibold px-2 items-center text-lg">
                        {{ unassignedCount }}
                    </p>
                </span>
            </div>

            <!-- <div v-if="filterInspections && !hideThings" class="w-full pt-4 flex justify-between items-center">
              <p class="my-3 text-xl text-slate-600 font-semibold">{{ filterInspections.length }} Trees</p>

              <div class="py-4">
                <button v-if="!hideThings" @click.prevent="downloadData()" class="bg-slate-100 hover:bg-slate-200 font-semibold hover:text-blue-500 text-blue-700 p-3 rounded-lg">
                  CSV Export
                </button>
              </div>
            </div> -->

            <div
                v-if="filterInspections && !hideThings"
                class="w-full pb-3 text-slate-600 flex justify-between items-end"
            >
                <div>
                    Showing <b>{{ filterInspections.length }}</b> of
                    <b>{{ filteredLength }}</b>
                    <span v-if="filterInspections.length > filteredLength"
                    >(filtered)</span
                    >
                </div>
                <div class="flex items-center">
                    <div
                        v-show="selectedFeatures.length > 0"
                        class="pr-2 border-r border-slate-300 flex items-center"
                    >
                        <div class="pr-2">
                            <strong>{{ selectedFeatures.length }}</strong>
                            Inspections Selected
                        </div>
                        <div ref="actions">
                            <button
                                @click.prevent="openActionsMenu()"
                                class="mr-2 bg-slate-100 hover:bg-slate-200 font-semibold text-slate-600 p-3 rounded-lg"
                            >
                                Actions
                            </button>

                            <div
                                v-if="openActions"
                                class="relative h-0 left-0"
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="menu-button"
                                tabindex="-1"
                            >
                                <div
                                    class="mt-1 py-1 z-10 absolute rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none w-56"
                                    role="none"
                                >
                                    <div
                                        @click="
                                            showBulkAssign = true;
                                            openActions = false;
                                        "
                                        class="cursor-pointer hover:bg-slate-100 text-slate-700 block px-4 py-2 text-sm"
                                        role="menuitem"
                                        tabindex="-1"
                                        id="menu-item-0"
                                    >
                                        Assign Selected Inspections
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="showMap"
                        class="py-4 px-4 border-r border-slate-300"
                    >
                        <button
                            @click.prevent="enablePolygonDraw()"
                            class="mr-2 bg-slate-100 hover:bg-slate-200 font-semibold text-slate-600 p-3 rounded-lg"
                        >
                            Draw Area
                        </button>
                        <button
                            @click.prevent="clearArea()"
                            class="px-4 py-3 bg-slate-100 rounded-lg font-medium cursor-pointer text-md text-blue-700 hover:text-blue-500"
                        >
                            Remove Area
                        </button>
                    </div>
                    <div class="py-4 pl-4">
                        <button
                            @click.prevent="showMap = !showMap"
                            class="mr-4 font-medium cursor-pointer text-md text-blue-700 hover:text-blue-500 bg-slate-100 p-3 hover:bg-slate-200 rounded-lg"
                        >
                            Toggle Map
                        </button>
                        <button
                            v-if="!hideThings"
                            @click.prevent="downloadData()"
                            class="bg-slate-100 hover:bg-slate-200 font-semibold hover:text-blue-500 text-blue-700 p-3 rounded-lg"
                        >
                            CSV Export
                        </button>
                    </div>
                </div>
            </div>

            <transition name="slide-down">
                <div :class="showMap ? 'visible map-height' : 'invisible h-0'">
                    <div id="map" class="pb-6" style="height: 70vh">
                        <address-search
                            class="relative w-full"
                            :toggleMap="baseMapSwitch"
                            :map="map"
                        >
                        </address-search>
                        <div id="popup" class="ol-popup">
                            <a
                                href="#"
                                id="popup-closer"
                                class="ol-popup-closer"
                            ></a>
                            <div id="popup-content" class="text-slate-600"></div>
                        </div>
                    </div>
                </div>
            </transition>

            <div
                v-if="filterInspections"
                class="ftree-table striped hovered main-first-cell w-full overflow-x-auto pt-4"
            >
                <div v-if="filterInspections.length" class="thead bg-slate-100 py-2">
                    <div class="tr py-2">
                        <div class="td items-center w-1/2 lg:w-1/6">
                            Tree ID
                        </div>
                        <div class="td items-center w-1/2 lg:w-1/6">
                            Approx Address
                        </div>
                        <div
                            :class="
                                hideThings ? 'w-1/2 lg:w-2/6' : 'w-1/2 lg:w-1/6'
                            "
                            class="td items-center"
                        >
                            Notes
                        </div>
                        <div
                            v-if="!hideThings"
                            class="td items-center w-1/2 lg:w-1/6"
                        >
                            Assign To
                        </div>
                        <div
                            @click.prevent="
                                sortBy = 'created_at';
                                sortAsc = !sortAsc;
                                sortArray();
                            "
                            class="td items-center w-1/2 lg:w-1/6 cursor-pointer"
                        >
                            Flagged
                        </div>
                        <div
                            @click.prevent="
                                sortBy = 'due_date';
                                sortAsc = !sortAsc;
                                sortArray();
                            "
                            class="td items-center w-1/2 lg:w-1/6 cursor-pointer"
                        >
                            Due
                        </div>
                    </div>
                </div>
                <p
                    v-if="!filterInspections.length"
                    class="text-slate-600 mt-3 mb-2"
                >
                    no flagged inspections.
                </p>
                <div v-else class="tbody text-left text-slate-700 pb-8">
                    <div v-for="tree in filterInspections" :key="tree.id">
                        <flagged-item
                            :tree="tree"
                            :index="tree.id"
                            :hideThings="hideThings"
                            :users="users"
                            :teams="teams"
                            :access="access"
                            :permissions="permissions"
                            @remove-flag="handleRemoveFlag"
                        />
                    </div>
                </div>
            </div>

            <p v-else class="text-slate-600 mt-3 mb-2">Fetching Data....</p>

            <bulk-assign-inspections
                v-if="showBulkAssign"
                :selectedFeatures="selectedFeatures"
                :users="users"
                :teams="teams"
                :permissions="permissions"
                :assignType="flagType === 'review' ? 13 : 1"
                v-model:showBulkAssign="showBulkAssign"
                @bulkUpdated="bulkUpdated"
            />
        </div>
        <!--    Completed Inspection page    -->
        <completed-inspections v-else-if="page === 2" :tasks="tasks"/>
    </div>
</template>
